import type { ModalNameType, ModalPayload } from '@/modals/components/ModalProvider';
import { IN_APP_NAVIGATION_STATE_KEY } from '@/utils/inAppNavigation';
import { MODAL_DATA_PARAM, MODAL_PARAM, PUSHED_MODAL_HISTORY_STATE } from '../consts';

type OpenModalOptions = {
  history?: 'push' | 'replace';
};

let modalNavigationVersion = 0;

export const getModalNavigationVersion = () => modalNavigationVersion;

type OpenModalArguments<TName extends ModalNameType>
  = ModalPayload<TName> extends void
    ? [modalData?: undefined, options?: OpenModalOptions]
    : undefined extends ModalPayload<TName>
      ? [modalData?: Exclude<ModalPayload<TName>, undefined>, options?: OpenModalOptions]
      : [modalData: ModalPayload<TName>, options?: OpenModalOptions];

const getModalUrl = (
  modalName: ModalNameType,
  modalData: unknown,
): string => {
  const params = new URLSearchParams();
  params.set(MODAL_PARAM, modalName);
  if (modalData != null) {
    params.set(MODAL_DATA_PARAM, JSON.stringify(modalData));
  }
  return `?${params.toString()}`;
};

const getModalHistoryState = (): Record<string, unknown> => {
  const state = window.history.state;
  if (!state || typeof state !== 'object') {
    return {};
  }

  const inAppIndex = (state as Record<string, unknown>)[IN_APP_NAVIGATION_STATE_KEY];
  return typeof inAppIndex === 'number' ? { [IN_APP_NAVIGATION_STATE_KEY]: inAppIndex } : {};
};

const hasActiveModalParam = (): boolean => {
  return new URLSearchParams(window.location.search).has(MODAL_PARAM);
};

function openModal<TName extends ModalNameType>(
  modalName: TName,
  ...args: OpenModalArguments<TName>
): void;
function openModal(
  modalName: ModalNameType,
  modalData?: unknown,
  options?: OpenModalOptions,
): void {
  const url = getModalUrl(modalName, modalData);
  modalNavigationVersion += 1;

  if (options?.history === 'replace' || (!options?.history && hasActiveModalParam())) {
    window.history.replaceState(getModalHistoryState(), '', url);
    return;
  }

  window.history.pushState({ ...getModalHistoryState(), [PUSHED_MODAL_HISTORY_STATE]: true }, '', url);
}

export default openModal;
